import { IClient } from 'client/IClientInfo'
import React, { useCallback, useEffect } from 'react'
import {
  reqEnterRegisterCode,
  reqLogin,
  reqLoginViaCookie,
  reqLogout,
  reqRegister,
} from './LoginRequests'
import LoginDialog from './login_dialog/LoginDialog'

type TLoginResponceCallback = (
  success: boolean,
  status?: string,
  tries?: number,
  isFirstLogin?: boolean
) => void

interface AuthContextType {
  user: IClient | null
  setUser: (client: IClient | null) => void
  signin: (
    login: string,
    password: string,
    isRemember: boolean,
    captcha: string,
    captchaHash: string,
    callback: TLoginResponceCallback
  ) => void
  register: (
    name: string,
    login: string,
    email: string,
    password: string,
    secondPassword: string,
    captcha: string,
    captchaHash: string,
    callback: (success: boolean, status?: string, userId?: number) => void
  ) => void
  enterRegisterCode: (
    userId: number,
    code: number,
    isRemember: boolean,
    callback: TLoginResponceCallback
  ) => void
  signinWithToken: (callback: TLoginResponceCallback) => void
  signout: (callback?: VoidFunction) => void
  showSignInDialog: VoidFunction
}

let AuthContext = React.createContext<AuthContextType>(null!)

function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<IClient | null>(null)
  const [showDialog, setShowDialog] = React.useState(false)

  const signin = useCallback(
    (
      login: string,
      password: string,
      isRemember: boolean,
      captcha: string,
      captchaHash: string,
      callback: TLoginResponceCallback
    ) => {
      reqLogin(login, password, isRemember, captcha, captchaHash, (response) => {
        if (response.status === 'OK' && response.user) {
          callback(true, response.status, -1, response.is_first_login)
          setUser(response.user)
        } else {
          callback(false, response.status, response.tries)
        }
      })
    },
    []
  )

  const register = useCallback(
    (
      name: string,
      phone: string,
      email: string,
      password: string,
      secondPassword: string,
      captcha: string,
      captchaHash: string,
      callback: (success: boolean, status?: string, userId?: number) => void
    ) => {
      reqRegister(name, phone, email, password, secondPassword, captcha, captchaHash, (response) => {
        if (response.status === 'OK') {
          callback(true, response.status, response.user_id)
          // setUser(response.user || null)
        } else {
          callback(false, response.status)
        }
      })
    },
    []
  )

  const enterRegisterCode = useCallback(
    (
      userId: number,
      code: number,
      isRemember: boolean,
      callback: TLoginResponceCallback
    ) => {
      reqEnterRegisterCode(userId, code, isRemember, (response) => {
        if (response.status === 'OK') {
          callback(true, response.status)
          setUser(response.user || null)
        } else {
          callback(false, response.status)
        }
      })
    },
    []
  )

  const signinWithToken = useCallback((callback: TLoginResponceCallback) => {
    reqLoginViaCookie((response) => {
      if (response.status === 'OK') {
        callback(true)
        setUser(response.user || null)
      } else {
        callback(false, response.status)
      }
    })
  }, [])

  const signout = useCallback((callback?: VoidFunction) => {
    reqLogout(() => {
      setUser(null)
      // document.location.replace('#/login')
      if (callback) callback()
    })
  }, [])

  const showSignInDialog = useCallback(() => {
    setShowDialog(true)
  }, [])

  const closeSignInDialog = useCallback(() => {
    setShowDialog(false)
  }, [])

  useEffect(() => {
    signinWithToken(() => {})
  }, [signinWithToken])

  const value = {
    user,
    setUser,
    signin,
    register,
    enterRegisterCode,
    signinWithToken,
    signout,
    showSignInDialog,
  }

  return (
    <AuthContext.Provider value={value}>
      <LoginDialog
        show={showDialog}
        onClose={closeSignInDialog}
        onLogin={signin}
        onRegister={register}
        onEnterCode={enterRegisterCode}
      />
      {children}
    </AuthContext.Provider>
  )
}

function useAuth() {
  return React.useContext(AuthContext)
}

export { AuthProvider, useAuth, AuthContext }
